import { BlockService } from '@blocksuite/block-std';
import type { SurfaceBlockModel } from '@blocksuite/blocks';
import { NoteDisplayMode } from '@blocksuite/blocks';
import type { YBlock } from '@blocksuite/store'; // Import YBlock type for the signal payload

// BlockModel might not be needed directly if we rely on signals passing the typed model
// import type { BlockModel } from '@blocksuite/store';
import type {
  EdgelessElementGroupsBlockModel,
  ManagedNoteElement,
  ManagedSurfaceElement,
} from './edgeless-group-model';

/**
 * Service for the EdgelessElementGroupsBlock.
 * Provides methods to interact with managed element groups at the editor level.
 * Manages initialization and toggling visibility.
 */
export class EdgelessElementGroupsBlockService extends BlockService<EdgelessElementGroupsBlockModel> {
  override mounted() {
    super.mounted();

    // Handle existing blocks on mount
    this.doc.getBlocksByFlavour('pique:edgeless-element-tab').forEach(block => {
      const model = block.model as EdgelessElementGroupsBlockModel;
      if (model) {
        // Ensure initialization only happens if not already done (check model state)
        if (
          !model.managedNoteElements$.value ||
          model.managedNoteElements$.value.length === 0
        ) {
          this.initializeManagedNoteElements(model);
        }
      }
    });

    // Subscribe to underlying block changes
    this.disposables.add(
      this.doc.slots.yBlockUpdated.on(this._handleYBlocksUpdated)
    );
  }

  override unmounted() {
    super.unmounted();
  }

  // Handler for underlying Yjs block changes (receives one update object at a time)
  private readonly _handleYBlocksUpdated = (
    update: { type: 'add' | 'delete'; id: string; yBlock?: YBlock } // Fix: Parameter is a single object
  ) => {
    // Remove outer transact and forEach loop
    if (update.type === 'add') {
      const block = this.doc.getBlock(update.id);
      if (block && block.flavour === 'pique:edgeless-element-tab') {
        const model = block.model as EdgelessElementGroupsBlockModel;
        // Ensure initialization only happens if not already done (check model state)
        if (
          !model.managedNoteElements$.value ||
          model.managedNoteElements$.value.length === 0
        ) {
          this.initializeManagedNoteElements(model);
        }
      }
    }
  };

  /**
   * Initializes the managed elements for a specific group block instance.
   * Finds initial notes, updates the model state, hides the notes,
   * and updates the service's internal state tracking.
   *
   * @param model The specific model instance to initialize.
   */
  public initializeManagedNoteElements(model: EdgelessElementGroupsBlockModel) {
    if (!model.doc) {
      console.error(
        '[EdgelessGroupService] initializeManagedNoteElements called but model.doc is not available.'
      );
      return;
    }

    // Check if already initialized by checking the model's state directly
    if (
      model.managedNoteElements$.value &&
      model.managedNoteElements$.value.length > 0
    ) {
      return;
    }

    const noteBlocksToManage: ManagedNoteElement[] = model.doc
      .getBlocksByFlavour('affine:note')
      .map(block => {
        const noteModel = block.model as { displayMode?: string } | undefined;
        const displayMode = noteModel?.displayMode;
        // Only manage notes that are currently meant to be visible in edgeless
        if (displayMode === NoteDisplayMode.DocAndEdgeless) {
          return {
            id: block.model.id,
            originalDisplayMode: NoteDisplayMode.DocAndEdgeless, // Store the original state
          };
        }
        return null;
      })
      .filter((el): el is NonNullable<typeof el> => el !== null);

    // Use a transaction ONLY if we are actually changing things
    if (noteBlocksToManage.length > 0) {
      model.doc.transact(() => {
        // Update the model state
        model.doc.updateBlock(model, {
          managedNoteElements: noteBlocksToManage,
        });
        noteBlocksToManage.forEach(managedEl => {
          const currentBlock = model.doc?.getBlock(managedEl.id);
          if (currentBlock && currentBlock.flavour === 'affine:note') {
            // Set initial state to DocOnly
            model.doc.updateBlock(currentBlock.model, {
              displayMode: NoteDisplayMode.DocOnly,
            });
          } else {
            console.warn(
              `[EdgelessGroupService] Initial block ${managedEl.id} disappeared or was not affine:note during transaction for block ${model.id}, skipping hide.`
            );
          }
        });
      });
    }

    const surfaceBlocksToManage: ManagedSurfaceElement[] = [];
    const elementsToHide: Array<[SurfaceBlockModel, string]> = [];

    // Get surface blocks and their elements
    model.doc.getBlocksByFlavour('affine:surface').forEach(block => {
      const surfaceModel = block.model as SurfaceBlockModel;
      const elementsMap = surfaceModel.elements.getValue();

      if (!elementsMap) return;

      // Convert YMap entries to array and process each element
      for (const [id, element] of elementsMap.entries()) {
        const props = Object.fromEntries(element.entries());
        surfaceBlocksToManage.push({
          id,
          properties: props,
          elementsArray: {},
        });
        // Track elements to remove
        elementsToHide.push([surfaceModel, id]);
      }
    });

    if (elementsToHide.length > 0) {
      model.doc.transact(() => {
        elementsToHide.forEach(([surfaceModel, id], index) => {
          if (surfaceModel.hasElementById(id)) {
            const element = surfaceModel.getElementById(id);
            const surfaceBlock = surfaceBlocksToManage[index];
            if (element && surfaceBlock) {
              surfaceBlock.elementsArray[id] = element.display;
              surfaceModel.updateElement(id, { display: false });
            }
          }
        });
      });
    }
  }

  /**
   * Toggles the visibility of the notes managed by a specific EdgelessElementGroupsBlock instance.
   *
   * @param blockId The ID of the EdgelessElementGroupsBlockModel instance.
   * @returns `true` if elements are now visible (restored), `false` if hidden (DocOnly), `null` on error or if no elements to toggle.
   */
  public toggleVisibility(blockId: string): boolean | null {
    const groupBlock = this.doc.getBlock(blockId);
    if (!groupBlock || groupBlock.flavour !== 'pique:edgeless-element-tab') {
      console.warn(
        `[EdgelessGroupService] Block with ID ${blockId} not found or not a group block.`
      );
      return null;
    }

    const groupModel = groupBlock.model as EdgelessElementGroupsBlockModel;
    // Use the model's current state for toggling
    const managedNoteElements = groupModel.managedNoteElements$.value;

    // Check if managedNoteElements exists and is not empty. If it's null/undefined, initialization might not have run.
    if (!managedNoteElements || managedNoteElements.length === 0) {
      // If managedNoteElements is an empty array, initialization ran but found nothing.
      // If it's null/undefined, it might not have run yet.
      return null;
    }

    const firstElement = managedNoteElements[0];
    if (!firstElement) return null;
    const firstElementId = firstElement.id;
    const firstBlock = this.doc.getBlock(firstElementId);

    // Handle case where the first block might not exist anymore
    if (!firstBlock) {
      console.warn(
        `[EdgelessGroupService] First managed element ${firstElementId} for block ${blockId} not found. Cannot determine current state.`
      );
      // Attempt to proceed assuming hidden state? Or return error? Let's return null.
      return null;
    }
    if (firstBlock.flavour !== 'affine:note') {
      console.warn(
        `[EdgelessGroupService] First managed element ${firstElementId} for block ${blockId} is not an affine:note. Cannot determine current state.`
      );
      return null;
    }

    const firstBlockModel = firstBlock.model as { displayMode?: string };

    const currentlyHidden =
      firstBlockModel.displayMode === NoteDisplayMode.DocOnly;

    const targetModeIsVisible = currentlyHidden;

    this.doc.transact(() => {
      managedNoteElements.forEach(managedEl => {
        const blockToToggle = this.doc.getBlock(managedEl.id);
        if (blockToToggle && blockToToggle.flavour === 'affine:note') {
          const newMode = targetModeIsVisible
            ? managedEl.originalDisplayMode
            : NoteDisplayMode.DocOnly;

          const currentMode = (blockToToggle.model as { displayMode?: string })
            ?.displayMode;

          // Only update if the mode needs changing
          if (currentMode !== newMode) {
            this.doc.updateBlock(blockToToggle.model, {
              displayMode: newMode,
            });
          }
        } else if (blockToToggle) {
          console.warn(
            `[EdgelessGroupService] Managed block ${managedEl.id} not found during toggle for ${blockId}, skipping.`
          );
        }
      });

      // Toggle surface elements
      this.doc.getBlocksByFlavour('affine:surface').forEach(block => {
        const surfaceModel = block.model as SurfaceBlockModel;
        const managedElements = groupModel.managedSurfaceElements$.value;
        managedElements?.forEach(managed => {
          const element = surfaceModel.getElementById(managed.id);
          if (element) {
            surfaceModel.updateElement(managed.id, {
              display: targetModeIsVisible
                ? managed.elementsArray[managed.id]
                : false,
            });
          }
        });
      });
    });

    return targetModeIsVisible;
  }
}
